import {
  garnishmentCapPercentageSchema,
  garnishmentListLimitSchema,
  garnishmentMoneyAmountSchema,
  garnishmentMutableStatusSchema,
  garnishmentOptionalNonEmptyString,
  garnishmentStatusSchema,
  garnishmentTypeSchema,
  schemes,
  zSchema,
} from "@zeal/common";

export const publicGarnishmentSchema = zSchema.object({
  garnishmentID: schemes.uuid({ description: "Garnishment ID" }),
  companyID: schemes.companyID(),
  employeeID: schemes.mongoObjectId("employeeID"),
  garnishment_type: garnishmentTypeSchema,
  total_amount_owed: garnishmentMoneyAmountSchema.optional(),
  total_amount_withheld: garnishmentMoneyAmountSchema,
  status: garnishmentStatusSchema,
  deductionTemplateID: schemes.uuid({ description: "Deduction template ID" }),
  effective_start_date: schemes.isoDate(),
  effective_end_date: schemes.isoDate().optional(),
  agency_name: zSchema.string().optional(),
  agency_address: zSchema.string().optional(),
  cap_amount: garnishmentMoneyAmountSchema.optional(),
  cap_percentage: garnishmentCapPercentageSchema.optional(),
  garnishment_state: schemes.stateCode().optional(),
  case_id: zSchema.string().optional(),
  order_number: zSchema.string().optional(),
  garnishment_document_id: zSchema.string().optional(),
  custom_name: zSchema.string().optional(),
  external_id: zSchema.string().optional(),
  satisfied_at: zSchema.string().optional(),
  created_at: zSchema.string(),
  updated_at: zSchema.string(),
});

export const createGarnishmentRequestSchema = zSchema.object({
  companyID: schemes.companyID(),
  employeeID: schemes.mongoObjectId("employeeID"),
  garnishment_type: garnishmentTypeSchema,
  total_amount_owed: garnishmentMoneyAmountSchema.optional(),
  effective_start_date: schemes.isoDate(),
  agency_name: schemes.nonEmptyString(
    "Agency name must be at least 1 character"
  ),
  agency_address: schemes.nonEmptyString(
    "Agency address must be at least 1 character"
  ),
  cap_amount: garnishmentMoneyAmountSchema.optional(),
  cap_percentage: garnishmentCapPercentageSchema.optional(),
  garnishment_state: schemes.stateCode().optional(),
  case_id: garnishmentOptionalNonEmptyString(
    "Case ID must be at least 1 character"
  ),
  order_number: garnishmentOptionalNonEmptyString(
    "Order number must be at least 1 character"
  ),
  garnishment_document_id: garnishmentOptionalNonEmptyString(
    "Garnishment document ID must be at least 1 character"
  ),
  custom_name: garnishmentOptionalNonEmptyString(
    "Custom name must be at least 1 character"
  ),
  external_id: garnishmentOptionalNonEmptyString(
    "External ID must be at least 1 character"
  ),
});

export const listGarnishmentsRequestSchema = zSchema.object({
  companyID: schemes.companyID().optional(),
  employeeID: schemes.mongoObjectId("employeeID").optional(),
  status: garnishmentStatusSchema.optional(),
  garnishment_type: garnishmentTypeSchema.optional(),
  external_id: garnishmentOptionalNonEmptyString(
    "External ID must be at least 1 character"
  ),
  start_at: schemes.uuid().optional(),
  limit: garnishmentListLimitSchema,
});

export const getGarnishmentRequestSchema = zSchema.object({
  garnishmentID: schemes.uuid({ description: "Garnishment ID" }),
});

export const patchGarnishmentRequestSchema = zSchema
  .object({
    garnishmentID: schemes.uuid({ description: "Garnishment ID" }),
    status: garnishmentMutableStatusSchema.optional(),
    effective_end_date: schemes.isoDate().optional(),
    custom_name: garnishmentOptionalNonEmptyString(
      "Custom name must be at least 1 character"
    ),
    cancelled_reason: garnishmentOptionalNonEmptyString(
      "Cancelled reason must be at least 1 character"
    ),
    garnishment_document_id: garnishmentOptionalNonEmptyString(
      "Garnishment document ID must be at least 1 character"
    ),
  })
  .refine(
    ({
      status,
      effective_end_date,
      custom_name,
      cancelled_reason,
      garnishment_document_id,
    }) =>
      status != null ||
      effective_end_date != null ||
      custom_name != null ||
      cancelled_reason != null ||
      garnishment_document_id != null,
    {
      message: "At least one garnishment field must be provided.",
    }
  );

export const listGarnishmentsResponseSchema = zSchema.array(
  publicGarnishmentSchema
);

export type TPublicGarnishment = zSchema.infer<typeof publicGarnishmentSchema>;

export type TCreateGarnishmentRequest = zSchema.infer<
  typeof createGarnishmentRequestSchema
>;

export type TListGarnishmentsRequest = zSchema.infer<
  typeof listGarnishmentsRequestSchema
>;

export type TGetGarnishmentRequest = zSchema.infer<
  typeof getGarnishmentRequestSchema
>;

export type TPatchGarnishmentRequest = zSchema.infer<
  typeof patchGarnishmentRequestSchema
>;

export type TListGarnishmentsResponse = zSchema.infer<
  typeof listGarnishmentsResponseSchema
>;
